import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../services/api';
import { createPkceAndStore } from '../utils/pkce';

const AZURE_CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID;

function buildInviteMicrosoftAuthUrl(codeChallenge: string, token: string): string {
  if (!AZURE_CLIENT_ID || typeof window === 'undefined') return '';
  const redirectUri = `${window.location.origin}/invite/callback`;
  return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${encodeURIComponent(AZURE_CLIENT_ID)}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&response_mode=query&scope=openid%20email%20profile&state=${encodeURIComponent(token)}&code_challenge=${encodeURIComponent(codeChallenge)}&code_challenge_method=S256`;
}

const Invite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleReady(true);
    document.head.appendChild(script);
    return () => {
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) document.head.removeChild(existingScript);
    };
  }, []);

  useEffect(() => {
    if (googleReady && window.google && googleButtonRef.current && token) {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id',
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: '100%'
      });
    }
    // eslint-disable-next-line
  }, [googleReady, token]);

  const formHeaders = { 'Content-Type': 'application/x-www-form-urlencoded' };

  async function handleCredentialResponse(response: any) {
    setLoading(true);
    setError(null);
    try {
      const formData = new URLSearchParams();
      formData.append('google_code', response.credential);
      await api.post(`/auth/invite/${token}/accept`, formData.toString(), { headers: formHeaders });
      setSuccess('Invitation accepted! You can now log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  }


  const hasMicrosoft = !!(AZURE_CLIENT_ID && token);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Paper sx={{ p: 4, minWidth: 350, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Accept Invitation</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {!success && (
          <>
            <Typography sx={{ mb: 2 }}>To accept your invitation, sign in with Google or Microsoft:</Typography>
            <Box ref={googleButtonRef} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }} />
            {hasMicrosoft && (
              <Button
                fullWidth
                variant="outlined"
                size="large"
                sx={{ mb: 2 }}
                onClick={async () => {
                  try {
                    const { codeChallenge } = await createPkceAndStore();
                    window.location.href = buildInviteMicrosoftAuthUrl(codeChallenge, token || '');
                  } catch (e) {
                    console.error('PKCE error:', e);
                    setError('Could not start Microsoft sign-in. Please try again.');
                  }
                }}
              >
                Sign in with Microsoft
              </Button>
            )}
            {loading && <CircularProgress size={24} />}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Invite; 